<template>
  <div>
    <!-- Modal de mise à jour système -->
    <UpdateModal
      :isOpen="isUpdateModalOpen"
      @update="handleUpdate"
    />

    <!-- Alerte hors ligne -->
    <MobileAlert />

    <Updating v-if="new_version" :newVersion="app_version" />
    <Promote v-if="set_promote_mode" />

    <!-- Background landing si c'est la page de landing -->
    <div
      v-show="this.$store.state.layout === 'landing'"
      class="landing-bg h-100 bg-gradient-primary position-fixed w-100"
    ></div>

    <!-- Barre de navigation latérale -->
    <sidenav
      :custom_class="this.$store.state.mcolor"
      :class="[
        this.$store.state.isTransparent,
        this.$store.state.isRTL ? 'fixed-end' : 'fixed-start',
      ]"
      v-if="this.$store.state.showSidenav"
    />

    <!-- Contenu principal -->
    <main
      class="main-content position-relative max-height-vh-100 h-100 border-radius-lg"
    >
      <!-- Navbar -->
      <navbar
        :class="[navClasses]"
        :textWhite="
          this.$store.state.isAbsolute ? 'text-white opacity-8' : 'text-white'
        "
        :minNav="navbarMinimize"
        v-if="this.$store.state.showNavbar"
      />

      <!-- Vue du routeur -->
      <router-view />

      <!-- Pied de page -->
      <app-footer v-show="this.$store.state.showFooter" />

      <!-- Configurateur -->
      <configurator
        :toggle="toggleConfigurator"
        :class="[
          this.$store.state.showConfig ? 'show' : '',
          this.$store.state.hideConfigButton ? 'd-none' : '',
        ]"
      />
    </main>
  </div>
</template>

<script>
import Sidenav from "./widget/Sidenav";
import Configurator from "@/widget/Configurator.vue";
import Navbar from "@/widget/Navbars/Navbar.vue";
import AppFooter from "@/widget/Footer.vue";
import MobileAlert from "@/views/components/MobileAlert.vue";
import Updating from "@/views/components/Updating.vue";
import Promote from "@/views/components/Promote.vue";
import UpdateModal from "@/components/UpdateModal.vue";

import { mapMutations } from "vuex";
import { loadModeFromApi } from "@/utils/classroomSetup";
import { installNetworkWatch } from "@/utils/network";
import { activateDarkMode, deactivateDarkMode } from "@/assets/js/dark-mode";
import { logoutUser } from "@/utils/auth";
import { getsystem_config } from "@/utils/services";

const UPDATE_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

export default {
  name: "App",
  data() {
    return {
      new_app_version: "",
      set_promote_mode: false,
      isUpdateModalOpen: false,
      updateInterval: null,
    };
  },
  components: {
    Sidenav,
    Configurator,
    Navbar,
    AppFooter,
    MobileAlert,
    Updating,
    Promote,
    UpdateModal
  },
  methods: {
    ...mapMutations(["toggleConfigurator", "navbarMinimize"]),
    startInactivityTimer() {
      let inactivityTime = function () {
        let time;
        window.onload = resetTimer;
        document.onmousemove = resetTimer;
        document.onkeypress = resetTimer;
        document.ontouchstart = resetTimer;

        function resetTimer() {
          clearTimeout(time);
          time = setTimeout(logoutUser, 6 * 60 * 60 * 1000);
        }
      };
      inactivityTime();
    },

    async getSystem_config() {
      try {
        const response = await getsystem_config();

        if (response && response.status === 200) {
          const onlineVersion = response.data.app_version; // Version récupérée depuis l'API
          const localVersion = localStorage.getItem("app_version"); // Version stockée localement

          // Si aucune version locale n'existe, on stocke la version du serveur sans afficher la modale
          if (!localVersion) {
            localStorage.setItem("app_version", onlineVersion);
          } else if (localVersion !== onlineVersion) {
            // Si la version locale existe et est différente de la version en ligne, on affiche la modale
            this.isUpdateModalOpen = true;
            this.new_app_version = onlineVersion; // Stocke la nouvelle version à appliquer
          }
          this.set_promote_mode = response.data.set_promote_mode || false;
        } else {
          console.error(
            "Données de configuration système invalides ou vides:",
            response
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des configurations système:",
          error
        );
      }
    },

    handleUpdate() {
      this.isUpdateModalOpen = false;
      localStorage.setItem("app_version", this.new_app_version);
      window.location.reload();
    },
    startUpdateTimer() {
      this.updateInterval = setInterval(() => {
        this.getSystem_config();
      }, UPDATE_INTERVAL_MS); // Utilise la constante
    },
  },
  computed: {
    navClasses() {
      return {
        "position-sticky bg-white left-auto top-2 z-index-sticky":
          this.$store.state.isNavFixed && !this.$store.state.darkMode,
        "position-sticky bg-default left-auto top-2 z-index-sticky":
          this.$store.state.isNavFixed && this.$store.state.darkMode,
        "position-absolute px-4 mx-0 w-100 z-index-2":
          this.$store.state.isAbsolute,
        "px-0 mx-4": !this.$store.state.isAbsolute,
      };
    },
  },
  beforeMount() {
    this.$store.state.isTransparent = "bg-transparent";
    if (this.$store.state.darkMode) {
      this.$store.state.darkMode = false;
      this.$store.state.sidebarType = "bg-white";
      deactivateDarkMode();
    } else {
      this.$store.state.darkMode = true;
      this.$store.state.sidebarType = "bg-default";
      activateDarkMode();
    }
  },
  async mounted() {
    /*
     * Surveillance de la connexion, posée avant tout appel réseau : c'est
     * elle qui affiche la carte d'avertissement pendant une coupure et la
     * carte verte au retour. Installée ici, elle vaut pour toute
     * l'application — aucun écran n'a à s'en occuper.
     */
    installNetworkWatch();

    this.startInactivityTimer();
    await this.getSystem_config();

    /*
     * Mode du module Classroom (École / Collaboratif).
     *
     * Chargé ici, au démarrage, et non plus seulement sur l'écran de
     * configuration : plusieurs fonctionnalités en dépendent (saisie d'un
     * paiement partiel, droits parents…). Un utilisateur arrivant
     * directement sur une liste d'élèves gardait sinon
     * `has_configured_classroom` à false, et les actions réservées au mode
     * collaboratif restaient silencieusement inertes.
     *
     * Sans `await` : rien ne doit retarder l'affichage de l'application, et
     * la valeur est réactive — l'interface se met à jour dès son arrivée.
     */
    loadModeFromApi();

    this.startUpdateTimer();
  },
};
</script>

<style>
.bg-y-gradient {
  background: linear-gradient(to bottom, #ffcd66, #fbad41);
}
body {
  overflow-x: hidden;
  font-family: "Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif !important;
}
</style>
